import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./ShiftPaymentModal.css";
import { RBModal } from "./RBModal";
import { RBEmptyBlock } from "./RBEmptyBlock";
import { RBCardView } from "./RBCardView";
import { RBTextField } from "./RBTextField";
import { RBButton } from "./RBButton";
import { Spacing, Colors } from "../theme/Theme";
import { ShiftProps } from "../interfacesProps/ShiftProps";
import { ShiftService } from "../api/ShiftService";
import { CalendarToday } from "@material-ui/icons";
import { FormControl, MenuItem, OutlinedInput, Select } from "@material-ui/core";

const RBDatePickerInput = React.forwardRef<HTMLInputElement, any>((props, ref) => {
  const { value, onClick, disabled, className} = props;
  return (
    <div className="rb-shift-payment-date-input-wrapper">
      <input
        ref={ref}
        value={value}
        onClick={onClick}
        readOnly
        disabled={disabled}
        className={`${className ?? ""} rb-shift-payment-date-input`.trim()}
      />
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="rb-shift-payment-calendar-button"
        aria-label="Open calendar"
        title="Open calendar"
      >
        <CalendarToday style={{ fontSize: 20 }} />
      </button>
    </div>
  );
});

export interface PaymentPayload {
  paymentDate: Date | null;
  paymentMethod: string;
  paymentReference: string;
  paymentOtherDescription?: string;
}

interface ShiftPaymentModalProps {
  open: boolean;
  onClose: () => void;
  shift?: ShiftProps | null;
  existingPayment?: any; // backend payment object (optional)
  currentUser?: any; // used to determine edit permission
  primaryColor?: string;
  secondaryColor?: string;
  onCreate?: (payload: PaymentPayload) => Promise<void> | void;
  onUpdate?: (payload: PaymentPayload) => Promise<void> | void;
}

const ShiftPaymentModal: React.FC<ShiftPaymentModalProps> = ({
  open,
  onClose,
  shift,
  existingPayment,
  currentUser,
  primaryColor,
  secondaryColor,
  onCreate,
  onUpdate,
}) => {
  const [paymentDate, setPaymentDate] = useState<Date | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [paymentReference, setPaymentReference] = useState<string>("");
  const [paymentOtherDescription, setPaymentOtherDescription] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [fetchedExistingPayment, setFetchedExistingPayment] = useState<any>(null);
  const [hasFetchedExistingPayment, setHasFetchedExistingPayment] = useState(false);

  const isOwnerCurrentUser =
    !!(currentUser as any)?.owner ||
    String((currentUser as any)?.roleName || "")
      .trim()
      .toLowerCase() === "owner";

  const activeExistingPayment = hasFetchedExistingPayment
    ? fetchedExistingPayment
    : existingPayment;

  const formatPaymentMethodLabel = (value: string) => {
    const normalized = String(value || "").trim().toLowerCase();
    const labelsByValue: Record<string, string> = {
      direct_deposit: "Direct Deposit",
      emt: "EMT",
      cheque: "Cheque",
      stripe: "Stripe",
      other: "Other",
    };
    return labelsByValue[normalized] ?? value;
  };

  const normalizePaymentMethod = (raw: any): string => {
    if (!raw) return "";
    const s = String(raw).trim();
    const key = s
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/-+/g, "_");

    if (key === "direct_deposit" || key === "directdeposit") return "direct_deposit";
    if (key === "emt" || key === "e_transfer" || key === "etransfer") return "emt";
    if (key === "cheque" || key === "check" || key === "cheq") return "cheque";
    if (key === "stripe" || key === "stripe_payment" || key === "stripepay") return "stripe";
    if (key === "other") return "other";

    // Fall back to original string if backend sends an unexpected value.
    return s;
  };

  const canEditExisting = () => {
    if (!activeExistingPayment) return true;
    if (normalizePaymentMethod(activeExistingPayment?.paymentMethod) === "stripe") {
      return false;
    }
    if (!currentUser) return false;
    return currentUser.id === activeExistingPayment.submittedByUserId;
  };

  // Backend stores paymentDate as an ISO timestamp, but the UI is date-only.
  // To avoid timezone-induced "previous day" display, treat paymentDate as a calendar day
  // and map it to a local midnight Date using the UTC date components.
  const coerceBackendPaymentDateForDisplay = (iso: any): Date | null => {
    if (!iso) return null;
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  };

  useEffect(() => {
    let isMounted = true;

    if (!open) {
      setHasFetchedExistingPayment(false);
      setFetchedExistingPayment(null);
      return () => {
        isMounted = false;
      };
    }

    const shiftId = (shift as any)?.id;
    if (!shiftId) {
      setHasFetchedExistingPayment(true);
      setFetchedExistingPayment(null);
      return () => {
        isMounted = false;
      };
    }

    (async () => {
      try {
        const payment = await ShiftService.getShiftPayment(String(shiftId));
        if (!isMounted) return;
        setFetchedExistingPayment(payment || null);
        console.log("Fetched existing payment for shift", shiftId, payment);
      } catch (_error) {
        if (!isMounted) return;
        setFetchedExistingPayment(null);
      } finally {
        if (isMounted) {
          setHasFetchedExistingPayment(true);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [open, shift]);

  const shiftAcceptedPaymentMethods = useMemo(() => {
    const defaultMethods =
      (shift as any)?.pharmacist?.networks?.[0]?.paymentConfig?.defaultWorkerPaymentMethods ||
      [];
    const acceptedMethods =
      (shift as any)?.pharmacist?.pharmacist?.acceptedPaymentMethods || [];

    const isPharmacistLogin =
      String((currentUser as any)?.roleName || "")
        .trim()
        .toLowerCase() === "pharmacist";

    const combinedMethods = isPharmacistLogin
      ? [...(Array.isArray(defaultMethods) ? defaultMethods : [])]
      : [
          ...(Array.isArray(defaultMethods) ? defaultMethods : []),
          ...(Array.isArray(acceptedMethods) ? acceptedMethods : []),
        ];

    const merged: string[] = [];
    combinedMethods
      .map((value: any) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean)
      .forEach((value: string) => {
        const normalized = normalizePaymentMethod(value);
        if (!normalized) return;
        if (!isOwnerCurrentUser && normalized === "stripe") return;
        if (!merged.includes(normalized)) {
          merged.push(normalized);
        }
      });

    return merged;
  }, [shift, currentUser, isOwnerCurrentUser]);

  const paymentMethodOptions = useMemo(() => {
    const options = shiftAcceptedPaymentMethods.map((value) => ({
      value,
      label: formatPaymentMethodLabel(value),
    }));
    const existingMethod = normalizePaymentMethod(activeExistingPayment?.paymentMethod);
    if (existingMethod && !options.some((option) => option.value === existingMethod)) {
      options.push({ value: existingMethod, label: formatPaymentMethodLabel(existingMethod) });
    }
    return options;
  }, [shiftAcceptedPaymentMethods, activeExistingPayment]);

  const hasPaymentMethodOptions = paymentMethodOptions.length > 0;

  const resetFromExistingPayment = () => {
    setPaymentDate(
      activeExistingPayment?.paymentDate
        ? coerceBackendPaymentDateForDisplay(activeExistingPayment.paymentDate)
        : null
    );
    setPaymentMethod(normalizePaymentMethod(activeExistingPayment?.paymentMethod) || "");
    setPaymentReference(activeExistingPayment?.paymentReference || "");
    setPaymentOtherDescription(activeExistingPayment?.paymentMethodOther || "");
  };

  const handleClose = () => {
    if (activeExistingPayment) {
      resetFromExistingPayment();
      setMode("view");
    }
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    if (activeExistingPayment) {
      resetFromExistingPayment();
      // Always open existing payments in view mode.
      setMode("view");
    } else {
      // default for new payment
      const shiftStartStr = (shift as any)?.startTime || (shift as any)?.officialStartTime || (shift as any)?.actualStartTime;
      const shiftStart = shiftStartStr ? new Date(shiftStartStr) : null;
      const today = new Date();
      let defaultDate = today;
      if (shiftStart && defaultDate < shiftStart) defaultDate = shiftStart;
      setPaymentDate(defaultDate);
      setPaymentMethod("");
      setPaymentReference("");
      setPaymentOtherDescription("");
      setMode("edit");
    }
  }, [open, activeExistingPayment, shift, currentUser]);

  const startOfLocalDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const isBeforeLocalDay = (a: Date | null, b: Date | null) => {
    if (!a || !b) return false;
    return startOfLocalDay(a).getTime() < startOfLocalDay(b).getTime();
  };

  const shiftStartStr = (shift as any)?.startTime || (shift as any)?.officialStartTime || (shift as any)?.actualStartTime;
  const shiftStartDate = shiftStartStr ? new Date(shiftStartStr) : null;
  const shiftStartDay = shiftStartDate ? startOfLocalDay(shiftStartDate) : null;
  const maxPaymentDate = undefined;

  const isValidDate = (d: Date | null) => {
    if (!d) return false;
    return !Number.isNaN(new Date(d).getTime());
  };

  const normalizedMethod = normalizePaymentMethod(paymentMethod);
  const isOtherSelected = normalizedMethod === "other";
  const isStripeSelected = normalizedMethod === "stripe";
  const connectAccountStatus = String(
    (shift as any)?.pharmacist?.pharmacist?.connectAccountStatus || ""
  )
    .trim()
    .toLowerCase();
  const isStripeAccountEligible =
    connectAccountStatus === "active" || connectAccountStatus === "restricted";
  const isStripePayDisabled = isStripeSelected && !isStripeAccountEligible;
  const selectedMethodAllowed = paymentMethodOptions.some(
    (option) => option.value === normalizedMethod
  );
  const otherDescriptionRequiredMissing =
    isOtherSelected && (paymentOtherDescription || "").trim().length === 0;
  const dateRequiredMissing =
    !isStripeSelected && (!isValidDate(paymentDate) || isBeforeLocalDay(paymentDate, shiftStartDate));
  const requiredFieldsMissing =
    !hasPaymentMethodOptions ||
    !normalizedMethod ||
    !selectedMethodAllowed ||
    dateRequiredMissing ||
    otherDescriptionRequiredMissing ||
    isStripePayDisabled;

  const handleOk = async () => {
    if (requiredFieldsMissing) return;

    const payload: PaymentPayload = {
      paymentDate: isStripeSelected ? null : paymentDate,
      paymentMethod: normalizePaymentMethod(paymentMethod),
      paymentReference: isStripeSelected ? "" : paymentReference,
      paymentOtherDescription: isOtherSelected ? paymentOtherDescription : "",
    };
    try {
      setSubmitting(true);
      if (activeExistingPayment && onUpdate) {
        await onUpdate(payload);
      } else if (!activeExistingPayment && onCreate) {
        await onCreate(payload);
      }
      handleClose();
    } catch (e) {
      // parent handles error display
    } finally {
      setSubmitting(false);
    }
  };

  const referenceCount = paymentReference?.length ?? 0;
  const otherMethodCount = paymentOtherDescription?.length ?? 0;
  const isViewOnlyExistingPayment = !!activeExistingPayment && !canEditExisting();
  const isEditingExistingPayment = !!activeExistingPayment && canEditExisting() && mode === "edit";
  const canEditFields = !activeExistingPayment ? true : isEditingExistingPayment;
  const isExistingStripePayment = !!activeExistingPayment && normalizePaymentMethod(activeExistingPayment?.paymentMethod) === "stripe";

  const formatEnteredByLabel = (submittedByType: any) => {
    const normalized = String(submittedByType || "").toLowerCase();
    if (normalized === "owner") return "Owner";
    if (normalized === "regionalmanager") {
      return "Regional Manager";
    }
    if (normalized === "worker") return "Worker";
    return "User";
  };

  const formatShortDate = (isoOrDate: any) => {
    const d = isoOrDate instanceof Date ? isoOrDate : isoOrDate ? new Date(isoOrDate) : null;
    if (!d || Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }).format(d);
  };

  const enteredByLabel = formatEnteredByLabel((activeExistingPayment as any)?.submittedByType);
  const enteredOnDate = formatShortDate(
    (activeExistingPayment as any)?.createdAt || (activeExistingPayment as any)?.updatedAt 
  );

  const toLocalDateKey = (d: Date | null | undefined) => {
    if (!d) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  };

  const hasExistingPaymentChanges = () => {
    if (!activeExistingPayment) return false;

    const originalDateKey = toLocalDateKey(
      activeExistingPayment?.paymentDate
        ? coerceBackendPaymentDateForDisplay(activeExistingPayment.paymentDate)
        : null
    );
    const currentDateKey = toLocalDateKey(paymentDate);

    const originalMethod = normalizePaymentMethod(activeExistingPayment?.paymentMethod) || "";
    const currentMethod = normalizePaymentMethod(paymentMethod) || "";

    const originalRef = (activeExistingPayment?.paymentReference || "") as string;
    const currentRef = (paymentReference || "") as string;

    const originalOther = (activeExistingPayment?.paymentMethodOther || "") as string;
    const currentOther = (paymentOtherDescription || "") as string;

    const dateChanged = originalDateKey !== currentDateKey;
    const methodChanged = originalMethod !== currentMethod;
    const referenceChanged = originalRef !== currentRef;

    const shouldCompareOther = currentMethod === "other" || originalMethod === "other";
    const otherChanged = shouldCompareOther ? originalOther !== currentOther : false;

    return dateChanged || methodChanged || referenceChanged || otherChanged;
  };

  return (
    <RBModal
      open={open}
      onClose={handleClose}
      modalTitle={"Shift Payment Details"}
      header={
        <RBEmptyBlock
          style={{
            width: "100%",
            height: Spacing.large,
            backgroundColor: Colors.defaultGrey,
            flexShrink: 0,
          }}
        />
      }
      children={
        <>
          <div style={{ backgroundColor: Colors.defaultGrey, display: "flex", flex: 1 }}>
            <RBCardView
              style={{
                flex: 1,
                marginLeft: Spacing.large,
                marginRight: Spacing.large,
                marginBottom: Spacing.smallest,
                paddingLeft: Spacing.large,
                paddingTop: Spacing.large,
                paddingRight: Spacing.large,
                paddingBottom: Spacing.large,
              }}
            >
              <div className="rb-shift-payment-form">
              {isViewOnlyExistingPayment && (enteredOnDate || enteredByLabel) && !isExistingStripePayment && (
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: 12,
                    fontSize: 12,
                    color: "rgba(0,0,0,0.55)",
                    lineHeight: "16px",
                  }}
                >
                  {enteredByLabel === "Owner"
                    ? `Entered at ${enteredOnDate || ""}`.trim()
                    : `Entered by ${enteredByLabel}${enteredOnDate ? ` on ${enteredOnDate}` : ""}`}
                </div>
              )}
              <div className="rb-shift-payment-row">
                <div className="rb-shift-payment-label">Payment Method *</div>
                <div className="rb-shift-payment-control">
                  <FormControl variant="outlined" style={{ width: "100%" }}>
                    <Select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as string)}
                      disabled={!canEditFields || !hasPaymentMethodOptions}
                      displayEmpty
                      renderValue={(selected: any) => {
                        if (!hasPaymentMethodOptions) {
                          return (
                            <span style={{ color: "rgba(0,0,0,0.45)" }}>
                              No methods available
                            </span>
                          );
                        }

                        if (!selected) {
                          return (
                            <span style={{ color: "rgba(0,0,0,0.45)" }}>
                              Select Type
                            </span>
                          );
                        }

                        const value = String(selected);
                        return formatPaymentMethodLabel(value);
                      }}
                      input={
                        <OutlinedInput
                          style={{
                            height: 48,
                            borderRadius: 14,
                            backgroundColor: "white",
                            fontSize: 16,
                          }}
                        />
                      }
                      MenuProps={{
                        getContentAnchorEl: null as any,
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        PaperProps: {
                          style: {
                            marginTop: 2,
                            borderRadius: 10,
                            backgroundColor: "#fff",
                            color: "#000",
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled style={{ fontSize: 16 }}>
                        {hasPaymentMethodOptions ? "Select Type" : "No methods available"}
                      </MenuItem>
                      {paymentMethodOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value} style={{ fontSize: 16 }}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>

              <div className="rb-shift-payment-row">
                <div className="rb-shift-payment-label">Payment Date *</div>
                <div className="rb-shift-payment-control">
                  <DatePicker
                    selected={paymentDate}
                    onChange={(d: Date | null) => setPaymentDate(d)}
                    dateFormat="yyyy/MM/dd"                   
                    minDate={shiftStartDay ?? undefined}
                    maxDate={maxPaymentDate}
                    className="rb-shift-payment-input"
                    wrapperClassName="rb-shift-payment-datepicker-wrapper"
                    disabled={!canEditFields || isStripeSelected}
                    customInput={<RBDatePickerInput />}
                  />
                </div>
              </div>

              {paymentMethod === "other" && (
                <div className="rb-shift-payment-row">
                  <div className="rb-shift-payment-label">Other Method *</div>
                  <div className="rb-shift-payment-control">
                    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                      <RBTextField
                        value={paymentOtherDescription}
                        onChange={(e: any) =>
                          setPaymentOtherDescription((e.target.value ?? "").slice(0, 50))
                        }
                        placeholder={"Describe method"}
                        disabled={!canEditFields}
                        style={{ borderRadius: 14, backgroundColor: "white" }}
                        multiline
                        rows={3}
                        inputProps={{
                          maxLength: 50,
                          style: {
                            paddingTop: "0px",
                            paddingRight: "0px",
                            paddingBottom: "0px",
                            paddingLeft: "0px",
                            fontSize: 16,
                            lineHeight: "20px",
                          },
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: 6,
                          fontSize: 12,
                          color: "rgba(0,0,0,0.6)",
                        }}
                      >
                        {otherMethodCount}/50
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="rb-shift-payment-row">
                <div className="rb-shift-payment-label">Reference </div>
                <div className="rb-shift-payment-control">
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <RBTextField
                      className="rb-shift-payment-reference"
                      value={paymentReference}
                      onChange={(e: any) =>
                        setPaymentReference((e.target.value ?? "").slice(0, 100))
                      }
                      placeholder={"Enter Note or Ref. ID"}
                      disabled={!canEditFields || isStripeSelected}
                      style={{ borderRadius: 14, backgroundColor: "white" }}
                      multiline
                      rows={3}
                      inputProps={{
                        maxLength: 100,
                        style: {
                          paddingTop: "0px",
                          paddingRight: "0px",
                          paddingBottom: "0px",
                          paddingLeft: "0px",
                          fontSize: 16,
                          lineHeight: "20px",
                        },
                      }}
                    />
                    {!isStripeSelected && !isExistingStripePayment && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: 6,
                          fontSize: 12,
                          color: "rgba(0,0,0,0.6)",
                        }}
                      >
                        {`${referenceCount}/100`}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {canEditFields && isStripePayDisabled && (
                <div
                  style={{
                    color: Colors.red,
                    fontSize: 14,
                    marginBottom: Spacing.small,
                  }}
                >
                  {/* Stripe is not active yet. The worker must complete Stripe setup/review before paying. */}
                </div>
              )}

              <div className="rb-shift-payment-actions">
                {isViewOnlyExistingPayment ? (
                  <>
                    <RBButton
                      buttonBackgroundColor={primaryColor || "#1976d2"}
                      text="Edit"
                      disabled
                      onClick={() => {}}
                      style={{ flex: 1, height: 48, borderRadius: 14, fontSize: 16 }}
                    />
                    <RBButton
                      buttonBackgroundColor={secondaryColor || "#9e9e9e"}
                      text="Close"
                      onClick={handleClose}
                      style={{ flex: 1, height: 48, borderRadius: 14, fontSize: 16 }}
                    />
                  </>
                ) : activeExistingPayment ? (
                  mode === "view" ? (
                    <>
                      <RBButton
                        buttonBackgroundColor={primaryColor || "#1976d2"}
                        text="Edit"
                        onClick={() => setMode("edit")}
                        style={{ flex: 1, height: 48, borderRadius: 14, fontSize: 16 }}
                      />
                      <RBButton
                        buttonBackgroundColor={secondaryColor || "#9e9e9e"}
                        text="Cancel"
                        onClick={handleClose}
                        style={{ flex: 1, height: 48, borderRadius: 14, fontSize: 16 }}
                      />
                    </>
                  ) : (
                    <>
                      <RBButton
                        buttonBackgroundColor={primaryColor || "#1976d2"}
                        isLoading={submitting}
                        disabled={
                          requiredFieldsMissing ||
                          !hasExistingPaymentChanges() ||
                          (!isStripeSelected && isBeforeLocalDay(paymentDate, shiftStartDate))
                        }
                        text={isStripeSelected ? "Pay" : "Ok"}
                        onClick={handleOk}
                        style={{ flex: 1, height: 48, borderRadius: 14, fontSize: 16 }}
                      />
                      <RBButton
                        buttonBackgroundColor={secondaryColor || "#9e9e9e"}
                        text="Cancel"
                        onClick={handleClose}
                        style={{ flex: 1, height: 48, borderRadius: 14, fontSize: 16 }}
                      />
                    </>
                  )
                ) : (
                  <>
                    <RBButton
                      buttonBackgroundColor={primaryColor || "#1976d2"}
                      isLoading={submitting}
                      disabled={
                        requiredFieldsMissing ||
                        (!isStripeSelected && isBeforeLocalDay(paymentDate, shiftStartDate))
                      }
                      text={isStripeSelected ? "Pay" : "Ok"}
                      onClick={handleOk}
                      style={{ flex: 1, height: 48, borderRadius: 14, fontSize: 16 }}
                    />
                    <RBButton
                      buttonBackgroundColor={secondaryColor || "#9e9e9e"}
                      text="Cancel"
                      onClick={handleClose}
                      style={{ flex: 1, height: 48, borderRadius: 14, fontSize: 16 }}
                    />
                  </>
                )}
              </div>
              </div>
            </RBCardView>
          </div>
        </>
      }
    />
  );
};

export default ShiftPaymentModal;
